import { useFloating, autoUpdate, offset, flip, shift } from '@floating-ui/react-dom';
import React, { createContext, useState, useContext, useId, useLayoutEffect, useRef } from 'react';
import { createPortal } from 'react-dom';

const FOCUSABLE_SELECTOR = 'button, a[href], input, select, textarea, [tabindex]:not([tabindex="-1"])';

const TooltipContext = createContext(null);
const useTooltipContext = () => {
    const context = useContext(TooltipContext);

    return context;
};

export function TooltipTrigger({ children, attributes, className, style, focusable = false }) {
    const context = useTooltipContext();
    const wrapperRef = useRef(null);

    useLayoutEffect(() => {
        if (focusable) return undefined;
        const target = wrapperRef.current?.querySelector(FOCUSABLE_SELECTOR);
        if (!target) return undefined;
        target.setAttribute('aria-describedby', context.tooltipId);
        return () => target.removeAttribute('aria-describedby');
    }, [focusable, context.tooltipId]);

    const open = () => context.setIsOpen(true);
    const close = () => context.setIsOpen(false);

    const defaultAttributes = {
        onMouseOver: open,
        onMouseLeave: close,
        onFocus: open,
        onBlur: close,
        onKeyDown: (e) => {
            if (e.key === 'Escape') close();
        },
        ...(focusable && { tabIndex: 0, 'aria-describedby': context.tooltipId }),
    };

    const finalAttributes = Object.assign({}, defaultAttributes, attributes);

    const setRef = (element) => {
        wrapperRef.current = element;
        context.refs.setReference(element);
    };

    return (
        <div ref={setRef} {...finalAttributes} className={className} style={style}>
            {React.Children.map(children, (child) => child && React.cloneElement(child))}
        </div>)
}

export function TooltipContent({ children }) {
    const context = useTooltipContext();

    const tooltipElement = context.isOpen ? (
        <div id={context.tooltipId} className={`tooltip tooltip-primary ${context.finalOptions.arrowPlacement}`} ref={context.refs.setFloating} style={context.floatingStyles} role="tooltip">
            <div className="tooltip-inner">
                {children}
            </div>
        </div>
    ) : (
        <div id={context.tooltipId} hidden>
            {children}
        </div>
    );

    return createPortal(tooltipElement, document.body);
}

export default function Tooltip({ children, options, placement = 'top', arrowPlacement = 'top in' }) {
    const [isOpen, setIsOpen] = useState(false);
    const tooltipId = useId();

    let defaultOptions = {
        placement: placement,
        arrowPlacement: arrowPlacement,
        middleware: [offset(10), flip(), shift()],
        whileElementsMounted: autoUpdate,
        open: isOpen,
        onOpenChange: setIsOpen,
    }

    var finalOptions = Object.assign([], defaultOptions, options);
    const { refs, floatingStyles } = useFloating(finalOptions);

    return (
        <TooltipContext.Provider value={{ isOpen, setIsOpen, floatingStyles, refs, finalOptions, tooltipId }}>
            {React.Children.map(children, (child) => child && React.cloneElement(child))}
        </TooltipContext.Provider>
    );
}
